/**
 * 69.2a (Foothold's intel, DIRECTION §0.8's 69.2; decision 145 (2)): objectives known roughly. With `GameConfig.roughStart`
 * each team's picture holds a track per capture point: a circle ROUGH.radiusPeople round a seeded offset of the true place
 * (the true place always inside it). The point resolves — its place and its holder — when one of the team's people sees it
 * under the eye-to-eye rule the bots see each other by (the weather's sight, then the terrain line: a person's eye to a
 * person standing on the point). After that its holder is what the team last saw, going stale on the awareness half-lives
 * ('evidence'). A side's own points are always known.
 *
 * One accessor, `knownObjectives`, for both commanders (113, 145 (2)): the bot commander and the table read it alike. Off,
 * it is the truth (every point resolved, its holder now), so a reader moved onto it changes nothing. The record is
 * `state.objectiveIntel`, stepped once a second; off, nothing is stored and nothing runs. Pure.
 */
import type { CapturePoint, GameState, Vec3 } from '../types';
import { PHYS_DT } from '../constants';
import { vecAdd, vecCross, vecDot, vecNormalize, vecScale } from '../math';
import { dcos, dsin } from './dmath';
import { hashKey, keyedRandom } from './random';
import { confidenceAfterSeconds } from './awareness';
import { eyePointAt } from './los';
import { personLineOfSight } from './fineTerrain';
import { weatherSees } from './perception';
import { weatherEffectHere } from './weather';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';

/** The rough circle's radius (people; a dial — 69.2a's proposal). */
export const ROUGH = { radiusPeople: 60 };

/** What a team has learned of one point: when it resolved, and its holder as last seen (`seenAt`). */
export interface ObjectiveIntel { siteId: number; resolvedAt: number; holder: number | null; seenAt: number }

/** A point as a team knows it. Unresolved: the circle (`surfaceDir` its centre, `radius` world units) and no holder. */
export interface KnownObjective {
  siteId: number;
  planetIndex: number;
  sector?: number;
  resolved: boolean;
  /** The true place once resolved; the circle's centre before. */
  surfaceDir: Vec3;
  /** The circle's radius in world units along the ground (0 once resolved). */
  radius: number;
  /** The holder as the team knows it (null: nobody's); undefined until resolved. */
  holder?: number | null;
  /** The tick the holder was seen, and how sure the team may still be of it (1 fresh … 0). */
  holderAt?: number;
  holderConfidence: number;
}

/** The circle's radius in world units. */
export function roughRadius(state: GameState): number {
  return ROUGH.radiusPeople * PERSON_BODY_HEIGHT * personSetOf(state).k;
}

/** The centre of `team`'s circle round `site`: the true place moved a seeded distance (uniform over the disc) along the ground. */
export function roughCentre(state: GameState, team: number, site: Pick<CapturePoint, 'id' | 'planetIndex' | 'surfaceDir'>): Vec3 {
  const planet = state.planets[site.planetIndex];
  const dir = vecNormalize(site.surfaceDir);
  const seed = hashKey(state.config.seed);
  const turn = 2 * Math.PI * keyedRandom(seed, 0x6a2a, team, site.id, 0);
  const arc = roughRadius(state) * Math.sqrt(keyedRandom(seed, 0x6a2a, team, site.id, 1)) / planet.radius;   // (radians along the ground)
  const axis = Math.abs(dir.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const e1 = vecNormalize(vecCross(axis, dir)), e2 = vecCross(dir, e1);
  const t = vecAdd(vecScale(e1, dcos(turn)), vecScale(e2, dsin(turn)));
  return vecNormalize(vecAdd(vecScale(dir, dcos(arc)), vecScale(t, dsin(arc))));
}

/** Does one of `team`'s living people see a person standing on `site` now (the weather's sight, then the terrain line)? */
export function teamSeesSite(state: GameState, team: number, site: Pick<CapturePoint, 'planetIndex' | 'surfaceDir'>, tick: number): boolean {
  const planet = state.planets[site.planetIndex];
  if (!planet) return false;
  const ps = personSetOf(state), seconds = tick * PHYS_DT;
  const there = eyePointAt(planet, vecNormalize(site.surfaceDir), ps.losEyeHeight);
  for (const u of state.players) {
    if (!u.alive || u.teamIndex !== team || u.planetIndex !== site.planetIndex) continue;
    const eye = eyePointAt(planet, vecNormalize(u.surfaceDir), ps.losEyeHeight);
    const sight = weatherEffectHere(state, u.planetIndex, u.surfaceDir, seconds).sight * ps.k;
    if (weatherSees(state, u.planetIndex, eye, there, seconds, sight) && personLineOfSight(eye, there, state)) return true;
  }
  return false;
}

/** The teams in play (each player's), in order. */
const teamsOf = (state: GameState): number[] => [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);

/**
 * Once a second (match.ts): each team's record. A point it holds, or one of its people sees, is resolved and its holder
 * seen now; the rest keep what the team last saw. A team's points are walked in id order; the first eye that sees one ends
 * its look.
 */
export function stepObjectiveIntel(state: GameState, tick: number): GameState {
  const sites = state.modeState.points?.sites;
  if (!state.config.roughStart || !sites?.length) return state;
  const intel: Record<number, ObjectiveIntel[]> = {};
  let changed = !state.objectiveIntel;
  for (const team of teamsOf(state)) {
    const was = state.objectiveIntel?.[team] ?? [];
    const now: ObjectiveIntel[] = [];
    for (const site of [...sites].sort((a, b) => a.id - b.id)) {
      const prev = was.find(x => x.siteId === site.id);
      if (site.owner === team || teamSeesSite(state, team, site, tick)) now.push({ siteId: site.id, resolvedAt: prev?.resolvedAt ?? tick, holder: site.owner, seenAt: tick });
      else if (prev) now.push(prev);
    }
    if (now.length !== was.length || now.some((x, i) => x !== was[i])) changed = true;
    intel[team] = now;
  }
  return changed ? { ...state, objectiveIntel: intel } : state;
}

/** Every capture point as `team` knows it at `tick`, in id order — the one accessor (the bot commander's and the table's). */
export function knownObjectives(state: GameState, team: number, tick: number): KnownObjective[] {
  const sites = [...(state.modeState.points?.sites ?? [])].sort((a, b) => a.id - b.id);
  const radius = roughRadius(state);
  return sites.map(site => {
    const base = { siteId: site.id, planetIndex: site.planetIndex, ...(site.sector !== undefined ? { sector: site.sector } : {}) };
    if (!state.config.roughStart || site.owner === team) return { ...base, resolved: true, surfaceDir: site.surfaceDir, radius: 0, holder: site.owner, holderAt: tick, holderConfidence: 1 };
    const rec = state.objectiveIntel?.[team]?.find(x => x.siteId === site.id);
    if (!rec) return { ...base, resolved: false, surfaceDir: roughCentre(state, team, site), radius, holderConfidence: 0 };
    return { ...base, resolved: true, surfaceDir: site.surfaceDir, radius: 0, holder: rec.holder, holderAt: rec.seenAt, holderConfidence: confidenceAfterSeconds('evidence', (tick - rec.seenAt) * PHYS_DT, 1) };
  });
}

/** The circle of `team`'s that `place` lies in (unresolved or not), the nearest centre first — a scout's "circle under its place". */
export function circleUnder(state: GameState, team: number, place: { planetIndex: number; surfaceDir: Vec3 }, tick: number): KnownObjective | undefined {
  const radius = roughRadius(state);
  const p = vecNormalize(place.surfaceDir);
  let best: KnownObjective | undefined, bestCos = -2;
  for (const k of knownObjectives(state, team, tick)) {
    if (k.planetIndex !== place.planetIndex) continue;
    const site = state.modeState.points!.sites.find(s => s.id === k.siteId)!;
    const centre = roughCentre(state, team, site);
    const c = vecDot(p, centre);
    const R = state.planets[k.planetIndex].radius;
    if (c >= dcos(radius / R) && c > bestCos) { bestCos = c; best = k; }
  }
  return best;
}

/**
 * 69.2a's follow-up (the coordinator: the switch hides the sites from every reader): capture points as `team` knows them,
 * for the bots' own readers (a soldier's point, the APC's and the technical's objective). Off, `sites` itself (the same
 * array, so nothing moves). On: an unresolved point stands at its circle's centre, nobody's as far as the side knows (so it
 * is one to take — walked to, its circle resolves on the way in); a resolved one at its place, its holder as last seen; a
 * capture under way shows only on a point the side holds or saw this second.
 */
export function sitesAsKnown(state: GameState, team: number, sites: readonly CapturePoint[], tick: number): CapturePoint[] {
  if (!state.config.roughStart) return sites as CapturePoint[];
  const known = new Map(knownObjectives(state, team, tick).map(k => [k.siteId, k] as const));
  return sites.map(site => {
    const k = known.get(site.id);
    if (!k) return site;
    const fresh = k.resolved && k.holderConfidence >= SEEN_NOW;
    return { ...site, surfaceDir: k.surfaceDir, owner: k.resolved ? k.holder ?? null : null, capturer: fresh ? site.capturer : null, progress: fresh ? site.progress : 0 };
  });
}
/** A holder this sure was seen within about the last two seconds ('evidence' halves every 30 s): its capture shows. */
const SEEN_NOW = 0.95;
